import { millisecondsInDay } from './constants';
import { getLocalDateParts } from './getLocalDateParts';
import { isDate } from './isDate';

/**
 * Returns the difference in calendar days between two dates in local time,
 * ignoring the time of day. Essentially replicates date-fns/differenceInCalendarDays.
 *
 * @param {Date|number} dateLeft  - The later date (or timestamp).
 * @param {Date|number} dateRight - The earlier date (or timestamp).
 * @returns {number}              - Number of calendar days (can be negative).
 */
export const differenceInCalendarDays = (
  dateLeft: Date | number,
  dateRight: Date | number,
): number => {
  const leftDate = isDate(dateLeft) ? dateLeft : new Date(dateLeft);
  const rightDate = isDate(dateRight) ? dateRight : new Date(dateRight);

  // if either is invalid, return NaN (rather than throwing RangeError)
  if (Number.isNaN(leftDate.getTime()) || Number.isNaN(rightDate.getTime())) {
    return NaN;
  }

  const { year: y1, month: m1, day: d1 } = getLocalDateParts(leftDate);
  const { year: y2, month: m2, day: d2 } = getLocalDateParts(rightDate);

  // `start of day` in local time for each date (ie. midnight)
  const startOfDayLeft = new Date(y1, m1 - 1, d1).getTime();
  const startOfDayRight = new Date(y2, m2 - 1, d2).getTime();
  const msDiff = startOfDayLeft - startOfDayRight;

  // convert to calendar days and round (to handle DST shifts, etc.)
  return Math.round(msDiff / millisecondsInDay);
};
